import { useEffect, useState } from 'react';
import { ChannelSetup } from '../components/ChannelSetup';
import { ProductImage } from '../components/ProductImage';
import { Button, CenterState, ErrorBanner, Screen, Spinner } from '../components/Ui';
import { ApiError, api, mediaUrl } from '../lib/api';
import { formatDate, plural } from '../lib/format';
import type { CollectionSummary, Me } from '../types';

type Props = {
  me: Me;
  onMeChange: (me: Me) => void;
  onCreate: () => void;
  onOpen: (id: string) => void;
  onStats: (id: string) => void;
};

export function HomeView({ me, onMeChange, onCreate, onOpen, onStats }: Props) {
  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const res = await api.listCollections();
      setCollections(res.collections);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить подборки');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <Screen>
      <header className="home-header">
        <h1>Подборки</h1>
        <p className="muted">Собери товары по ссылкам и опубликуй пост в канал.</p>
      </header>

      {me.isAuthor ? (
        <>
          <ChannelSetup
            channels={me.channels}
            onChanged={(channels) => onMeChange({ ...me, channels })}
          />

          <Button full onClick={onCreate}>
            + Создать подборку
          </Button>
        </>
      ) : (
        <ErrorBanner>
          Доступ к созданию подборок закрыт. Приложение работает в приватном режиме.
        </ErrorBanner>
      )}

      {error && <ErrorBanner onRetry={() => void load()}>{error}</ErrorBanner>}

      {collections === null && !error && (
        <CenterState>
          <Spinner />
        </CenterState>
      )}

      {collections?.length === 0 && (
        <CenterState>
          <p className="muted">Пока пусто. Первая подборка займёт минуту.</p>
        </CenterState>
      )}

      <div className="collection-list">
        {collections?.map((item) => (
          <div key={item.id} className="collection-item">
            <button className="collection-item__main" onClick={() => onOpen(item.id)}>
              <div className="collection-item__cover">
                {item.coverMedia ? (
                  <img
                    className="product-image"
                    src={mediaUrl(item.coverMedia.url)}
                    alt={item.coverMedia.originalName}
                  />
                ) : item.cover ? (
                  <ProductImage
                    imageUrl={item.cover.imageUrl}
                    proxyUrl={item.cover.imageProxyUrl}
                    alt={item.cover.title}
                  />
                ) : (
                  <div className="product-image product-image--empty">
                    <span>—</span>
                  </div>
                )}
              </div>
              <div className="collection-item__body">
                <div className="collection-item__title">
                  {item.captionPreview?.trim() || 'Без текста'}
                </div>
                <div className="collection-item__meta">
                  {item.cardsCount} {plural(item.cardsCount, ['товар', 'товара', 'товаров'])}
                  {item.mediaCount > 0 &&
                    ` · ${item.mediaCount} ${plural(item.mediaCount, ['файл', 'файла', 'файлов'])}`}
                  {' · '}
                  {item.clicks} {plural(item.clicks, ['переход', 'перехода', 'переходов'])}
                  {' · '}
                  {formatDate(item.createdAt)}
                </div>
                <span
                  className={`chip ${item.status === 'published' ? 'chip--ok' : 'chip--draft'}`}
                >
                  {item.status === 'published' ? 'опубликована' : 'черновик'}
                </span>
              </div>
            </button>

            {/* Число открытий прямо в списке: чтобы посмотреть, как идут дела,
                не надо никуда проваливаться — а нажатие открывает разбор. */}
            {me.isAuthor && (
              <button
                className="collection-item__stats"
                onClick={() => onStats(item.id)}
                aria-label={`Статистика подборки «${item.captionPreview?.trim() || 'без текста'}»`}
              >
                <span className="collection-item__stats-value">{item.views}</span>
                <span className="collection-item__stats-caption">открытий</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </Screen>
  );
}
